import { useNavigate, useSearch } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";

import { ORDERS_COPY } from "./orders";
import { OrdersPane } from "./OrdersPane";
import { ShippingPane } from "./ShippingPane";

export type Pane = "orders" | "shipping";
export const PANES: Pane[] = ["shipping", "orders"];
/** The keys only the Orders pane writes: its period, sort, page, open order, its Show and Refunds filters and its two old place keys. */
export const ORDERS_KEYS = ["pp_order", "pp_opage", "pp_sort", "pp_dir", "pp_period", "pp_from", "pp_to", "f_show", "f_refunds", "f_shop", "f_ship"] as const;
/** The pane a URL opens: the one it names; else Shipping, the default (C1 v1.36, as the approved prototype), unless the URL
 * carries a key only the Orders pane writes: a link saved while Orders was the default named no pane, and still opens Orders. */
export function paneOf(search: Record<string, unknown>): Pane {
  if (search.pp_pane === "orders" || search.pp_pane === "shipping") return search.pp_pane;
  return ORDERS_KEYS.some((key) => search[key] != null && search[key] !== "") ? "orders" : "shipping";
}
/** The URL for a pane: always named, so a key the other pane left in the URL never decides it. */
export function searchForPane(search: Record<string, unknown>, pane: Pane): Record<string, unknown> {
  return { ...search, pp_pane: pane };
}

/** The Shopify Economics tab: two panes as a segmented control, Shipping (the default since C1 v1.36) and Orders. */
export function ShopifyEconomicsTab({ areaName }: { areaName: string }) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const pane = paneOf(search);
  const go = (next: Pane) => void navigate({ to: ".", search: searchForPane(search, next), replace: true });
  return (
    <div className="flex flex-col gap-3">
      <div role="group" aria-label="Pane" className="flex gap-1">
        {PANES.map((key) => (
          <Button key={key} size="sm" variant={key === pane ? "default" : "outline"} aria-pressed={key === pane} data-nonmutating onClick={() => go(key)}>
            {ORDERS_COPY.panes[key]}
          </Button>
        ))}
      </div>
      {pane === "orders" ? <OrdersPane areaName={areaName} /> : <ShippingPane areaName={areaName} />}
    </div>
  );
}
